import { MutableRefObject, useRef, useState } from "react"
import './MultiHexKeyExercise.scss'
import { NoteButtons } from "../../../Comps/NoteButtons/NoteButtons"
import { Music } from "../../../Util/Music/Music"
import { HexMap } from "../../../Data/HexMap/HexMap"
import { HexMapBuilderParams, BuildHexMap } from "../../../Data/HexMap/HexMapBuilder"
import { HexMapModifier } from "../../../Data/HexMap/HexMapModifier"
import { All } from "../../../Util/Constants/Quantities"
import { produce } from "immer"
import { Note } from "../../../Data/HexMap/Note"
import { HxMapGroup } from "../../../Comps/HxMap/HxMapGroup"
import { TransformTranslate } from "../../../Util/Transform/Transform"
import { OutlineBox } from "../../../Comps/OutlineBox/OutlineBox"
import { KeySelectors, PositionSelectors } from "./HexKeyMemorizerComponents"
import { ExerciseMessage } from "../Utils/Message/ExerciseMessage"
import { ClassNames } from "../../../Util/ClassNames"
import { useNotePlayer } from "../../../Sound/SoundStore"
import { NotePlayer } from "../../../Sound/NotePlayer"

export function HexKeyMemorizer(){
    const notePlayer:MutableRefObject<NotePlayer> = useRef(useNotePlayer())
    const started:MutableRefObject<boolean> = useRef(false)
    const allPositions:number[] = [1,2,3,4,5,6]
    const answeredPositions:MutableRefObject<number[]> = useRef([])
    const lastAnsweredPosition:MutableRefObject<number|undefined> = useRef(undefined)
    const answeredKeys:MutableRefObject<number[]> = useRef([])
    const selectedPosition:MutableRefObject<number> = useRef(0)
    const selectedKeyTonic:MutableRefObject<number|undefined> = useRef(undefined)
    const selectedKeys:MutableRefObject<number[]> = useRef<number[]>([])
    const [selectedKeysUpdate, setSelectedKeysUpdate] = useState<number>(0)
    const updateSelectedKeys = () => {
        setSelectedKeysUpdate(Date.now())
    }
    const [lastAnswerWasCorrect, setLastAnswerWasCorrect] = useState<boolean|null>(null)
    const [message, setMessage] = useState('Select keys to memorize then click Start')
    const getKeyTonic = () => {
        if(selectedKeyTonic.current === undefined){return Music.G}
        return selectedKeyTonic.current
    }
    const getMap = () => {
        const keyTonic:number = getKeyTonic()
        const params:HexMapBuilderParams = {
            rows:1, columns:1, tonic:keyTonic, radius:90
        }
        const map:HexMap = BuildHexMap(params)
        const mod:HexMapModifier = new HexMapModifier(map)
        mod.notes_setDefined([All], false)
        if(started.current){
            const definedPositions:number[] = [0]; //.concat(answeredPositions.current)
            if(lastAnsweredPosition.current){
                definedPositions.push(lastAnsweredPosition.current)
            }
            mod.notes_setDefined(definedPositions, true)
            mod.notes_setSelectedState(definedPositions, Note.SelectedState_Selected)
            mod.notes_setSelectedState([selectedPosition.current], Note.SelectedState_Hilited)

        }        
        return map
    }
    const [map, setMap] = useState(getMap())
    const getAvailableKeys = ():number[] => {
        const availableKeys:number[] = selectedKeys.current.filter((value:number)=>{
            return(!answeredKeys.current.includes(value))
        })
        return availableKeys
    }
    const getRandomKey = () => {
        const availableKeys:number[] = getAvailableKeys()
        const randomIndex:number = Math.floor(Math.random() * availableKeys.length)
        const keyTonic:number = availableKeys[randomIndex]
        return keyTonic
    }
    const getAvailablePositions = ():number[] => {
        const availablePositions:number[] = allPositions.filter((value:number)=>{
            return(!answeredPositions.current.includes(value))
        })
        return availablePositions
    }
    const getRandomPosition = () => {
        const availablePositions:number[] = getAvailablePositions()
        const randomIndex:number = Math.floor(Math.random() * availablePositions.length)
        return availablePositions[randomIndex]
    }
    const restart = () => {
        started.current = false
        setMessage('Select one or more keys first.')
        selectedKeys.current = [] 
        selectedKeyTonic.current = undefined 
        setMap(getMap())   
    }
    const start = () => {
        if(selectedKeys.current.length === 0){
            setMessage('Select one or more keys first.')
            return
        }
        started.current = true
        answeredPositions.current = []
        answeredKeys.current = []
        selectedKeyTonic.current = getRandomKey()
        selectedPosition.current = getRandomPosition()
        setMessage('Identify the highlighted note.')
        setMap(getMap())
    }
    const onKeyClick = () => {}
    const onNoteClick = () => {}
    const nextKey = () => {
        const keyTonic:number = getKeyTonic()
        answeredKeys.current.push(keyTonic)
        if(answeredKeys.current.length === selectedKeys.current.length){
            answeredKeys.current = []
        }
        selectedKeyTonic.current = getRandomKey()
        notePlayer.current.setTonic(selectedKeyTonic.current)
        answeredPositions.current = []
        lastAnsweredPosition.current = undefined
        selectedPosition.current = getRandomPosition()
        setMessage('Identify the highlighted note.')
        setLastAnswerWasCorrect(null)
        setMap(getMap())
    }
    const addKey = (keyTonic:number) => {
        selectedKeys.current.push(keyTonic)
        updateSelectedKeys()
        if(!started.current){
            setMessage('Key added. Select more or click Start to begin.')
        }
    }
    const removeKey = (keyTonic:number) => {
            const keyIndex:number = selectedKeys.current.indexOf(keyTonic)
            selectedKeys.current.splice(keyIndex,1)
            updateSelectedKeys()
            if(selectedKeys.current.length === 0){
                restart()
                return
            }
            if(keyTonic === selectedKeyTonic.current){
                nextKey()
                return
            }
            
    }
    const onKeySelect = (keyTonic:number) => {
        const keyIndex:number = selectedKeys.current.indexOf(keyTonic)
        if(keyIndex === -1){
            addKey(keyTonic)
        }
        else{
            removeKey(keyTonic)
        }
    }
    const onNoteSelect = (noteTonic:number) => {
        if(started){
            const selectedNoteTonic:number = Music.getKeyScale(getKeyTonic())[selectedPosition.current]
            if(selectedNoteTonic === noteTonic){
                onCorrect(selectedPosition.current, noteTonic)

                return
            }
            onIncorrect()
        }
    }
    const onCorrect = (answeredPosition:number, noteTonic:number) => {
        setMessage('Correct!')
        notePlayer.current.playNote(noteTonic, true)
        setLastAnswerWasCorrect(true)
        answeredPositions.current.push(answeredPosition)
        lastAnsweredPosition.current = answeredPosition
        if(answeredPositions.current.length === 6){
            setTimeout(()=>{
                    nextKey()
                }, 1000)
        }
        else{
            setTimeout(()=>{
                    lastAnsweredPosition.current = undefined
                    selectedPosition.current = getRandomPosition()
                    setMap(getMap())
                    setMessage('Identify the highlighted note.')
                    setLastAnswerWasCorrect(null)
                }, 1000)
        }
        setMap(getMap())
    }
    const onIncorrect = () => {
        notePlayer.current.playBuzzer()
        setMessage('Incorrect. Try again.')
        setLastAnswerWasCorrect(false)
    }
    const getHexClassNames = () => {
        if(!started.current){
            return('ghosted')
        }
        return ''
    }
    const getStartButtonClassNames = () => {
        const classNames:ClassNames = new ClassNames(['start-button-container'])
        if(selectedKeys.current.length === 0){
            classNames.addClass('ghosted')
        }
        return classNames.toString()
    }
    return(
        <OutlineBox title='HexKey Memorizer' width={600}>
            <div className={'multi-hex-key-exercise'}>
                <KeySelectors update={selectedKeysUpdate} onKeyClick={onKeySelect} currentKeyTonic={selectedKeyTonic.current} selectedKeys={selectedKeys.current}/>
                <div><ExerciseMessage message={message} answerStatus={lastAnswerWasCorrect}/></div>
                <div className={getHexClassNames()}>
                    <svg width={300} height={300}>
                        <g transform={TransformTranslate(150,150)}>
                            <HxMapGroup onKeyClick={onKeyClick} onNoteClick={onNoteClick} hexMap={map}/>
                        </g>
                    </svg>
                </div> 
                {started.current && <NoteButtons onClick={onNoteSelect}/>}
                {!started.current && <div className={getStartButtonClassNames()}><button className={'start-button'} type='button' onClick={start}>Start</button></div>}

            </div>
                    </OutlineBox>
    )
}

